import * as React from "react";

import * as Perseus from "@khan/component-perseus/perseus";
import type {DiagramData} from "@khan/guide-types";

import {validateDiagramContent} from "./validation.ts";

interface Props {
    // The stringified Perseus JSON content to render as a diagram
    diagramData: DiagramData;
}

/**
 * Renders Perseus JSON content as a diagram using only interactive-graph widgets.
 * Only supports Perseus items, not articles. The content should reference
 * interactive-graph widgets using Perseus widget placeholders (e.g. "[[☃ interactive-graph 1]]").
 * Expects widgets with 'static: true' and 'correct' fields. Triangle diagrams
 * support 'showSides: true', 'showAngles: true' for angle labels, and 'lockedFigures.labels'
 * for vertex labels positioned at specific coordinates.
 * Validates the JSON and returns null if the content is invalid. Throws an error if the content
 * appears to be an article or contains non-interactive-graph widgets.
 */
export const Diagram = ({diagramData}: Props) => {
    const {code} = diagramData;

    const parsedContent = React.useMemo(() => {
        return validateDiagramContent(code);
    }, [code]);

    // Return null for invalid content
    if (!parsedContent) {
        return null;
    }

    // Render the content with embedded widgets (expected to be interactive-graph)
    return (
        <Perseus.Renderer
            content={parsedContent.question.content}
            widgets={parsedContent.question.widgets}
        />
    );
};
